import React, { useCallback, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  FormatSize as FontSizeIcon,
} from '@mui/icons-material';
import type { DeviceFieldFormatRule, DeviceFieldDisplayType, DeviceFieldColorEntry, ThresholdBand } from './types';

const DISPLAY_TYPES: { value: DeviceFieldDisplayType; label: string; hint: string }[] = [
  { value: 'text', label: 'Text', hint: 'Plain text with optional prefix/suffix' },
  { value: 'badge', label: 'Badge', hint: 'Colored badge based on value' },
  { value: 'elapsed_time', label: 'Elapsed Time', hint: 'Human-readable time ago' },
  { value: 'number', label: 'Number', hint: 'Numeric with prefix/suffix' },
  { value: 'progress', label: 'Progress Bar', hint: 'Visual progress bar' },
  { value: 'link', label: 'Link', hint: 'Clickable URL or template link' },
  { value: 'boolean', label: 'Boolean', hint: 'Status indicator for true/false values' },
  { value: 'datetime', label: 'Date / Time', hint: 'Formatted date and time display' },
  { value: 'threshold', label: 'Threshold', hint: 'Number with color-coded ranges' },
  { value: 'tag_list', label: 'Tag List', hint: 'Split value into individual tags' },
  { value: 'image', label: 'Image', hint: 'Thumbnail from a URL field' },
  { value: 'coordinate', label: 'Coordinate', hint: 'Lat/Lng with copy button' },
  { value: 'hidden', label: 'Hidden', hint: 'Do not show this field' },
];

interface Props {
  rules: DeviceFieldFormatRule[];
  onChange: (rules: DeviceFieldFormatRule[]) => void;
  availableFields: string[];
  fieldValuesMap?: Record<string, string[]>;
}

const DeviceListFormatBuilder: React.FC<Props> = ({ rules, onChange, availableFields, fieldValuesMap }) => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const usedFields = useMemo(() => new Set(rules.map((r) => r.field)), [rules]);
  const unusedFields = useMemo(
    () => availableFields.filter((f) => !usedFields.has(f)),
    [availableFields, usedFields],
  );

  const addRule = useCallback(
    (field?: string) => {
      const f = field || unusedFields[0];
      if (!f) return;
      const id = `rule_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const newRule: DeviceFieldFormatRule = {
        id,
        field: f,
        label: f,
        displayType: 'text',
        order: rules.length,
      };
      onChange([...rules, newRule]);
      setExpanded(id);
    },
    [rules, onChange, unusedFields],
  );

  const autoPopulate = useCallback(() => {
    const newRules: DeviceFieldFormatRule[] = availableFields.map((f, i) => ({
      id: `rule_${Date.now()}_${i}`,
      field: f,
      label: f,
      displayType: 'text' as DeviceFieldDisplayType,
      order: i,
    }));
    onChange(newRules);
  }, [availableFields, onChange]);

  const updateRule = useCallback(
    (id: string, patch: Partial<DeviceFieldFormatRule>) => {
      onChange(rules.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    },
    [rules, onChange],
  );

  const removeRule = useCallback(
    (id: string) => {
      onChange(rules.filter((r) => r.id !== id).map((r, i) => ({ ...r, order: i })));
    },
    [rules, onChange],
  );

  const moveRule = useCallback(
    (id: string, direction: -1 | 1) => {
      const idx = rules.findIndex((r) => r.id === id);
      if (idx < 0) return;
      const target = idx + direction;
      if (target < 0 || target >= rules.length) return;
      const next = [...rules];
      [next[idx], next[target]] = [next[target], next[idx]];
      onChange(next.map((r, i) => ({ ...r, order: i })));
    },
    [rules, onChange],
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Field Format Rules
        </Typography>
        <Stack direction="row" spacing={1}>
          {rules.length === 0 && availableFields.length > 0 && (
            <Button size="small" variant="outlined" onClick={autoPopulate} sx={{ fontSize: 11 }}>
              Auto-populate
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => addRule()}
            disabled={unusedFields.length === 0}
            sx={{ fontSize: 11 }}
          >
            Add field
          </Button>
        </Stack>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Define how each field appears on device list cards. When format rules are defined they replace
        the default detail fields rendering.
      </Typography>

      {rules.length === 0 && (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No format rules defined. Click &quot;Auto-populate&quot; to start from available fields,
            or add fields individually.
          </Typography>
        </Box>
      )}

      {rules.map((rule) => (
        <Accordion
          key={rule.id}
          expanded={expanded === rule.id}
          onChange={(_, isExpanded) => setExpanded(isExpanded ? rule.id : false)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            '&:not(:last-child)': { mb: 0.5 },
            '&::before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center' } }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', mr: 1 }}>
              <DragIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'grab' }} />
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 80 }}>
                {rule.label || rule.field}
              </Typography>
              <Chip
                label={DISPLAY_TYPES.find((d) => d.value === rule.displayType)?.label || rule.displayType}
                size="small"
                sx={{ fontSize: 10, height: 20 }}
              />
              {rule.span && (
                <Chip label="SPAN" size="small" variant="outlined" sx={{ fontSize: 8, height: 16, borderColor: 'info.main', color: 'info.main' }} />
              )}
              {rule.displayType === 'badge' && rule.colorMap && rule.colorMap.length > 0 && (
                <Stack direction="row" spacing={0.25}>
                  {rule.colorMap.slice(0, 4).map((c, i) => (
                    <Box key={i} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c.bg }} />
                  ))}
                  {rule.colorMap.length > 4 && (
                    <Typography variant="caption" color="text.disabled">+{rule.colorMap.length - 4}</Typography>
                  )}
                </Stack>
              )}
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Move up">
                <span>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); moveRule(rule.id, -1); }} disabled={rule.order === 0}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700 }}>▲</Typography>
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); moveRule(rule.id, 1); }} disabled={rule.order === rules.length - 1}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700 }}>▼</Typography>
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeRule(rule.id); }}>
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1}>
                <Autocomplete
                  size="small"
                  value={rule.field}
                  options={[rule.field, ...unusedFields]}
                  onChange={(_, v) => v && updateRule(rule.id, { field: v })}
                  renderInput={(params) => <TextField {...params} label="Source field" />}
                  sx={{ flex: 1 }}
                  freeSolo
                  disableClearable
                />
                <TextField
                  size="small"
                  label="Display label"
                  value={rule.label || ''}
                  onChange={(e) => updateRule(rule.id, { label: e.target.value })}
                  sx={{ flex: 1 }}
                />
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Select
                  size="small"
                  value={rule.displayType}
                  onChange={(e) => updateRule(rule.id, { displayType: e.target.value as DeviceFieldDisplayType })}
                  sx={{ flex: 1 }}
                >
                  {DISPLAY_TYPES.map((dt) => (
                    <MenuItem key={dt.value} value={dt.value}>
                      <Stack>
                        <Typography variant="body2">{dt.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{dt.hint}</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={rule.span ?? false}
                      onChange={(_, checked) => updateRule(rule.id, { span: checked })}
                    />
                  }
                  label={<Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Span</Typography>}
                  sx={{ ml: 0, mr: 0 }}
                />
              </Stack>

              {rule.displayType !== 'hidden' && (
                <StyleToolbar rule={rule} onUpdate={(patch) => updateRule(rule.id, patch)} />
              )}

              {(rule.displayType === 'text' || rule.displayType === 'number' || rule.displayType === 'threshold') && (
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Prefix"
                    value={rule.prefix || ''}
                    onChange={(e) => updateRule(rule.id, { prefix: e.target.value })}
                    sx={{ flex: 1 }}
                    placeholder="e.g. Speed: "
                  />
                  <TextField
                    size="small"
                    label="Suffix"
                    value={rule.suffix || ''}
                    onChange={(e) => updateRule(rule.id, { suffix: e.target.value })}
                    sx={{ flex: 1 }}
                    placeholder="e.g.  mph"
                  />
                </Stack>
              )}

              {rule.displayType === 'progress' && (
                <TextField
                  size="small"
                  label="Max value"
                  type="number"
                  value={rule.progressMax ?? 100}
                  onChange={(e) => updateRule(rule.id, { progressMax: Number(e.target.value) || 100 })}
                  helperText="The value at which the bar is full"
                />
              )}

              {rule.displayType === 'link' && (
                <TextField
                  size="small"
                  label="Link template"
                  value={rule.linkTemplate || ''}
                  onChange={(e) => updateRule(rule.id, { linkTemplate: e.target.value })}
                  placeholder="e.g. https://maps.google.com/?q={latitude},{longitude}"
                  helperText="Use {fieldName} placeholders. Leave blank to use raw field value as URL."
                />
              )}

              {rule.displayType === 'badge' && (
                <ColorMapEditor
                  colorMap={rule.colorMap || []}
                  onChange={(cm) => updateRule(rule.id, { colorMap: cm })}
                  fieldValues={fieldValuesMap?.[rule.field] || []}
                />
              )}

              {rule.displayType === 'boolean' && (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      label="True label"
                      value={rule.trueLabel || ''}
                      onChange={(e) => updateRule(rule.id, { trueLabel: e.target.value })}
                      placeholder="Yes"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="False label"
                      value={rule.falseLabel || ''}
                      onChange={(e) => updateRule(rule.id, { falseLabel: e.target.value })}
                      placeholder="No"
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>True color</Typography>
                    <Box
                      component="input"
                      type="color"
                      value={rule.trueColor || '#4caf50'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRule(rule.id, { trueColor: e.target.value })}
                      sx={{ width: 28, height: 28, border: 'none', cursor: 'pointer', borderRadius: 0.5, p: 0 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, ml: 1 }}>False color</Typography>
                    <Box
                      component="input"
                      type="color"
                      value={rule.falseColor || '#f44336'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRule(rule.id, { falseColor: e.target.value })}
                      sx={{ width: 28, height: 28, border: 'none', cursor: 'pointer', borderRadius: 0.5, p: 0 }}
                    />
                  </Stack>
                </Stack>
              )}

              {rule.displayType === 'datetime' && (
                <Select
                  size="small"
                  value={rule.dateFormat || 'datetime'}
                  onChange={(e) => updateRule(rule.id, { dateFormat: e.target.value as DeviceFieldFormatRule['dateFormat'] })}
                  fullWidth
                >
                  <MenuItem value="datetime">Date &amp; Time</MenuItem>
                  <MenuItem value="date">Date only</MenuItem>
                  <MenuItem value="time">Time only</MenuItem>
                  <MenuItem value="relative">Relative (e.g. 5m ago)</MenuItem>
                </Select>
              )}

              {rule.displayType === 'threshold' && (
                <ThresholdBandEditor
                  bands={rule.thresholdBands || []}
                  onChange={(bands) => updateRule(rule.id, { thresholdBands: bands })}
                />
              )}

              {rule.displayType === 'tag_list' && (
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    label="Separator"
                    value={rule.tagSeparator || ''}
                    onChange={(e) => updateRule(rule.id, { tagSeparator: e.target.value })}
                    placeholder="Default: comma, pipe, semicolon"
                    helperText="Regex pattern. Leave blank for , | ;"
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>Tag bg</Typography>
                    <Box
                      component="input"
                      type="color"
                      value={rule.tagBg || '#455a64'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRule(rule.id, { tagBg: e.target.value })}
                      sx={{ width: 28, height: 28, border: 'none', cursor: 'pointer', borderRadius: 0.5, p: 0 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50, ml: 1 }}>Tag text</Typography>
                    <Box
                      component="input"
                      type="color"
                      value={rule.tagFg || '#ffffff'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRule(rule.id, { tagFg: e.target.value })}
                      sx={{ width: 28, height: 28, border: 'none', cursor: 'pointer', borderRadius: 0.5, p: 0 }}
                    />
                  </Stack>
                </Stack>
              )}

              {rule.displayType === 'image' && (
                <TextField
                  size="small"
                  label="Thumbnail height (px)"
                  type="number"
                  value={rule.imageHeight ?? 48}
                  onChange={(e) => updateRule(rule.id, { imageHeight: Number(e.target.value) || 48 })}
                  helperText="Height in pixels. Image scales proportionally."
                />
              )}

              {rule.displayType === 'coordinate' && (
                <Autocomplete
                  size="small"
                  value={rule.coordField2 || ''}
                  options={availableFields}
                  onChange={(_, v) => updateRule(rule.id, { coordField2: v || '' })}
                  renderInput={(params) => (
                    <TextField {...params} label="Second coordinate field (longitude)" helperText="Optional. First field is latitude." />
                  )}
                  freeSolo
                  disableClearable
                />
              )}

            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

/* ─── Compact Style Toolbar ───────────────────────────────────────── */

const FONT_SIZE_CYCLE: Array<DeviceFieldFormatRule['fontSize']> = ['small', 'normal', 'large'];

interface StyleToolbarProps {
  rule: DeviceFieldFormatRule;
  onUpdate: (patch: Partial<DeviceFieldFormatRule>) => void;
}

const StyleToolbar: React.FC<StyleToolbarProps> = ({ rule, onUpdate }) => {
  const nextSize = () => {
    const cur = rule.fontSize || 'normal';
    const idx = FONT_SIZE_CYCLE.indexOf(cur);
    onUpdate({ fontSize: FONT_SIZE_CYCLE[(idx + 1) % FONT_SIZE_CYCLE.length] });
  };

  return (
    <Stack
      direction="row"
      spacing={0}
      alignItems="center"
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        px: 0.25,
        py: 0.25,
        bgcolor: 'action.hover',
      }}
    >
      <Tooltip title="Bold">
        <IconButton
          size="small"
          onClick={() => onUpdate({ bold: !rule.bold })}
          sx={{ color: rule.bold ? 'primary.main' : 'text.disabled', p: 0.5 }}
        >
          <BoldIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Italic">
        <IconButton
          size="small"
          onClick={() => onUpdate({ italic: !rule.italic })}
          sx={{ color: rule.italic ? 'primary.main' : 'text.disabled', p: 0.5 }}
        >
          <ItalicIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Box sx={{ width: '1px', height: 18, bgcolor: 'divider', mx: 0.5 }} />

      <Tooltip title="Align left">
        <IconButton
          size="small"
          onClick={() => onUpdate({ align: 'left' })}
          sx={{ color: (rule.align || 'left') === 'left' ? 'primary.main' : 'text.disabled', p: 0.5 }}
        >
          <AlignLeftIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Align center">
        <IconButton
          size="small"
          onClick={() => onUpdate({ align: 'center' })}
          sx={{ color: rule.align === 'center' ? 'primary.main' : 'text.disabled', p: 0.5 }}
        >
          <AlignCenterIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Align right">
        <IconButton
          size="small"
          onClick={() => onUpdate({ align: 'right' })}
          sx={{ color: rule.align === 'right' ? 'primary.main' : 'text.disabled', p: 0.5 }}
        >
          <AlignRightIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Box sx={{ width: '1px', height: 18, bgcolor: 'divider', mx: 0.5 }} />

      <Tooltip title={`Size: ${rule.fontSize || 'normal'}`}>
        <IconButton size="small" onClick={nextSize} sx={{ p: 0.5 }}>
          <FontSizeIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontSize: 8, ml: 0.25, textTransform: 'uppercase' }}>
            {(rule.fontSize || 'normal')[0]}
          </Typography>
        </IconButton>
      </Tooltip>

      <Box sx={{ width: '1px', height: 18, bgcolor: 'divider', mx: 0.5 }} />

      <Tooltip title="Text color (leave default for theme color)">
        <Box
          component="input"
          type="color"
          value={rule.textColor || '#ffffff'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ textColor: e.target.value })}
          sx={{ width: 20, height: 20, border: 'none', cursor: 'pointer', borderRadius: 0.5, p: 0, mx: 0.25 }}
        />
      </Tooltip>
      {rule.textColor && (
        <Tooltip title="Reset color">
          <IconButton size="small" onClick={() => onUpdate({ textColor: undefined })} sx={{ p: 0.25, fontSize: 10 }}>
            ✕
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

interface ColorMapEditorProps {
  colorMap: DeviceFieldColorEntry[];
  onChange: (colorMap: DeviceFieldColorEntry[]) => void;
  fieldValues?: string[];
}

const PRESET_COLORS = [
  { bg: '#4caf50', fg: '#fff' },
  { bg: '#f44336', fg: '#fff' },
  { bg: '#ff9800', fg: '#000' },
  { bg: '#2196f3', fg: '#fff' },
  { bg: '#9c27b0', fg: '#fff' },
  { bg: '#546e7a', fg: '#fff' },
  { bg: '#e91e63', fg: '#fff' },
  { bg: '#00bcd4', fg: '#fff' },
];

const ColorMapEditor: React.FC<ColorMapEditorProps> = ({ colorMap, onChange, fieldValues = [] }) => {
  const addEntry = () => {
    const preset = PRESET_COLORS[colorMap.length % PRESET_COLORS.length];
    onChange([...colorMap, { value: '', bg: preset.bg, fg: preset.fg }]);
  };

  const updateEntry = (idx: number, patch: Partial<DeviceFieldColorEntry>) => {
    onChange(colorMap.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };

  const removeEntry = (idx: number) => {
    onChange(colorMap.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="caption" fontWeight={600}>Color mapping</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addEntry} sx={{ fontSize: 11 }}>
          Add color
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Map specific field values to badge colors. Unmatched values use a default gray badge.
      </Typography>
      <Stack spacing={0.5}>
        {colorMap.map((entry, i) => (
          <Stack key={i} direction="row" spacing={0.5} alignItems="center">
            <Autocomplete
              size="small"
              freeSolo
              value={entry.value}
              options={fieldValues}
              onChange={(_, v) => updateEntry(i, { value: typeof v === 'string' ? v : '' })}
              onInputChange={(_, v, reason) => {
                if (reason !== 'reset') updateEntry(i, { value: v });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or type a value"
                  inputProps={{ ...params.inputProps, style: { fontSize: 12 } }}
                />
              )}
              sx={{ flex: 1 }}
              disableClearable
            />
            <Tooltip title="Background color">
              <Box
                component="input"
                type="color"
                value={entry.bg}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEntry(i, { bg: e.target.value })}
                sx={{ width: 28, height: 28, border: 'none', cursor: 'pointer', borderRadius: 0.5, p: 0 }}
              />
            </Tooltip>
            <Tooltip title="Text color">
              <Box
                component="input"
                type="color"
                value={entry.fg}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEntry(i, { fg: e.target.value })}
                sx={{ width: 28, height: 28, border: 'none', cursor: 'pointer', borderRadius: 0.5, p: 0 }}
              />
            </Tooltip>
            <Chip
              label={entry.value || '?'}
              size="small"
              sx={{ bgcolor: entry.bg, color: entry.fg, fontSize: 10, fontWeight: 700, minWidth: 40 }}
            />
            <IconButton size="small" onClick={() => removeEntry(i)}>
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

/* ─── Threshold Band Editor ──────────────────────────────────────── */

interface ThresholdBandEditorProps {
  bands: ThresholdBand[];
  onChange: (bands: ThresholdBand[]) => void;
}

const THRESHOLD_PRESETS = ['#4caf50', '#ff9800', '#f44336', '#2196f3', '#9c27b0'];

const ThresholdBandEditor: React.FC<ThresholdBandEditorProps> = ({ bands, onChange }) => {
  const addBand = () => {
    const color = THRESHOLD_PRESETS[bands.length % THRESHOLD_PRESETS.length];
    const nextMax = bands.length > 0 ? (bands[bands.length - 1].max + 50) : 50;
    onChange([...bands, { max: nextMax, color, label: '' }]);
  };

  const updateBand = (idx: number, patch: Partial<ThresholdBand>) => {
    onChange(bands.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  };

  const removeBand = (idx: number) => {
    onChange(bands.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="caption" fontWeight={600}>Threshold bands</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addBand} sx={{ fontSize: 11 }}>
          Add band
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Value &le; max gets the band color. Evaluated low to high.
      </Typography>
      <Stack spacing={0.5}>
        {bands.map((band, i) => (
          <Stack key={i} direction="row" spacing={0.5} alignItems="center">
            <TextField
              size="small"
              type="number"
              value={band.max}
              onChange={(e) => updateBand(i, { max: Number(e.target.value) || 0 })}
              label="Max"
              sx={{ width: 80 }}
              inputProps={{ style: { fontSize: 12 } }}
            />
            <TextField
              size="small"
              value={band.label || ''}
              onChange={(e) => updateBand(i, { label: e.target.value })}
              placeholder="Label (optional)"
              sx={{ flex: 1 }}
              inputProps={{ style: { fontSize: 12 } }}
            />
            <Tooltip title="Band color">
              <Box
                component="input"
                type="color"
                value={band.color}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateBand(i, { color: e.target.value })}
                sx={{ width: 28, height: 28, border: 'none', cursor: 'pointer', borderRadius: 0.5, p: 0 }}
              />
            </Tooltip>
            <IconButton size="small" onClick={() => removeBand(i)}>
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default DeviceListFormatBuilder;
